import { useReadContract } from 'wagmi';
import { formatEther, parseEther } from 'viem';
import { CONTRACT_ADDRESSES } from '../config/contracts';
import { useFlarePriceFTSO } from './useFlarePriceFTSO';

const ROUTER_ABI = [
  {
    type: 'function',
    name: 'getAmountsOut',
    stateMutability: 'view',
    inputs: [
      { type: 'uint256' },
      { type: 'address[]' }
    ],
    outputs: [
      { type: 'uint256[]' }
    ]
  }
];

/**
 * Fetches the current $EMBER price from the DEX
 * @returns {Object} { priceInFLR, priceInUSD, isLoading }
 */
export function useEmberPrice() {
  // Get live FLR price from Flare's FTSO oracle (decentralized, on-chain)
  const { flrPriceUSD } = useFlarePriceFTSO();
  // Query: How much FLR do you get for 1 EMBER?
  const { data: amountsOut, isLoading, error } = useReadContract({
    address: CONTRACT_ADDRESSES.ROUTER,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: CONTRACT_ADDRESSES.EMBER_TOKEN && CONTRACT_ADDRESSES.WFLR
      ? [
          parseEther('1'), // 1 EMBER
          [CONTRACT_ADDRESSES.EMBER_TOKEN, CONTRACT_ADDRESSES.WFLR]
        ]
      : undefined,
    query: {
      enabled: !!(CONTRACT_ADDRESSES.EMBER_TOKEN && CONTRACT_ADDRESSES.WFLR && CONTRACT_ADDRESSES.ROUTER),
      refetchInterval: 15000, // Refresh every 15 seconds
    }
  });

  // Log errors for debugging (skip expected "execution reverted" errors)
  if (error) {
    const isExpectedError = error.message?.includes('execution reverted') ||
      error.message?.includes('INSUFFICIENT_LIQUIDITY') ||
      error.message?.includes('INVALID_PATH');

    if (!isExpectedError) {
      console.error('[useEmberPrice] Router call failed:', error);
    }
  }

  if (!amountsOut || amountsOut.length < 2) {
    let errorReason = 'Unknown';
    if (error) {
      if (error.message?.includes('INSUFFICIENT_LIQUIDITY')) {
        errorReason = 'No liquidity in EMBER/WFLR pool';
      } else if (error.message?.includes('INVALID_PATH')) {
        errorReason = 'EMBER/WFLR pair does not exist';
      } else {
        errorReason = error.shortMessage || error.message || 'Router query failed';
      }
    } else if (!amountsOut) {
      errorReason = 'No response from router';
    }

    return {
      priceInFLR: null,
      priceInUSD: null,
      isLoading,
      error: errorReason
    };
  }

  const priceInFLR = Number(formatEther(amountsOut[1]));
  const priceInUSD = flrPriceUSD ? priceInFLR * flrPriceUSD : null;

  // Check if router returned zero/invalid values
  if (priceInFLR === 0 || !isFinite(priceInFLR)) {
    console.warn('[useEmberPrice] Router returned zero or invalid price - pool may be empty');
    return {
      priceInFLR: null,
      priceInUSD: null,
      isLoading,
      error: 'Pool has insufficient liquidity'
    };
  }

  // If we have EMBER/FLR price but can't get USD price, still return what we have
  if (!flrPriceUSD) {
    return {
      priceInFLR,
      priceInUSD: null,
      isLoading,
      error: 'FLR price unavailable'
    };
  }

  return {
    priceInFLR,
    priceInUSD,
    isLoading,
    error: null
  };
}
